// src/components/Header.js
// Main site header — includes navigation, theme toggle, cart, account,
// currency selector, and Google Translate widget.
// Uses --banner-height CSS variable set by AnnouncementBanner so it
// always sits exactly below the banner on any device.

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { ShoppingBag, Sun, Moon, Menu, X, User, ChevronDown, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';

const Header = ({ theme, toggleTheme }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { cartCount, setIsOpen } = useCart();
    const { isLoggedIn, customer } = useAuth();

    const { currency, currencies, changeCurrency } = useCurrency();
    const { count: wishlistCount } = useWishlist();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
    const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);
    const [isMobileCollectionsOpen, setIsMobileCollectionsOpen] = useState(false);

    // Collections shown in the nav dropdown. Add future collections here as siblings.
    const collections = [
        { label: 'Atlas', path: '/scents' },
    ];

    const isActive = (path) => location.pathname === path;
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const handleAccountClick = () => {
        navigate(isLoggedIn ? '/account' : '/login');
    };

    return (
        <>
            <header
                className="fixed left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
                style={{ top: 'var(--banner-height, 34px)' }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">

                    {/* Left — Hamburger (mobile only) */}
                    <div className="flex items-center md:hidden w-[40px]">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="text-foreground/80 hover:text-[#CEAC8F] transition-colors"
                            aria-label="Toggle menu"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Center — Logo */}
                    <Link
                        to="/"
                        className="absolute left-1/2 transform -translate-x-1/2 md:relative md:left-auto md:transform-none text-lg sm:text-2xl font-light tracking-[0.12em] sm:tracking-wider text-[#CEAC8F] hover:opacity-80 transition-opacity whitespace-nowrap"
                        style={{ fontFamily: "'Tenor Sans', sans-serif" }}
                    >
                        MAISON D'OUD
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-3 sm:gap-6">
                        <Link to="/scents" className={`text-xs sm:text-sm tracking-wide transition-colors ${isActive('/scents') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                            Our Scents
                        </Link>

                        {/* Collections dropdown — Atlas today, future collections join as siblings */}
                        <div className="relative">
                            <button
                                onClick={() => setIsCollectionsOpen(!isCollectionsOpen)}
                                className="flex items-center gap-1 text-xs sm:text-sm tracking-wide text-foreground/80 hover:text-[#CEAC8F] transition-colors"
                                aria-label="Browse collections"
                            >
                                Collections
                                <ChevronDown className="w-3 h-3" />
                            </button>

                            {isCollectionsOpen && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setIsCollectionsOpen(false)} />
                                    <div className="absolute left-0 top-8 z-20 bg-background border border-border rounded-lg shadow-xl py-1 min-w-[140px]">
                                        {collections.map((collection) => (
                                            <Link
                                                key={collection.label}
                                                to={collection.path}
                                                onClick={() => setIsCollectionsOpen(false)}
                                                className="block w-full text-left px-4 py-2 text-xs sm:text-sm text-foreground/80 hover:text-[#CEAC8F] transition-colors"
                                            >
                                                {collection.label}
                                            </Link>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        <Link to="/quiz" className={`text-xs sm:text-sm tracking-wide transition-colors ${isActive('/quiz') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                            Find Your Scent
                        </Link>

                        <Link to="/locations" className={`text-xs sm:text-sm tracking-wide transition-colors ${isActive('/locations') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                            Find Us
                        </Link>

                        <Link to="/contact" className={`text-xs sm:text-sm tracking-wide transition-colors ${isActive('/contact') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                            Get in touch
                        </Link>

                        {/* Currency selector dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                                className="flex items-center gap-1 text-xs text-foreground/80 hover:text-[#CEAC8F] transition-colors"
                                aria-label="Change currency"
                            >
                                {currency.symbol} {currency.code}
                                <ChevronDown className="w-3 h-3" />
                            </button>

                            {isCurrencyOpen && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setIsCurrencyOpen(false)} />
                                    <div className="absolute right-0 top-8 z-20 bg-background border border-border rounded-lg shadow-xl py-1 min-w-[120px]">
                                        {currencies.map((c) => (
                                            <button
                                                key={c.code}
                                                onClick={() => { changeCurrency(c.code); setIsCurrencyOpen(false); }}
                                                className={`w-full text-left px-4 py-2 text-xs transition-colors hover:text-[#CEAC8F] ${
                                                    currency.code === c.code ? 'text-[#CEAC8F]' : 'text-foreground/80'
                                                }`}
                                            >
                                                {c.label}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Theme toggle */}
                        <button onClick={toggleTheme} className="text-foreground/80 hover:text-[#CEAC8F] transition-colors" aria-label="Toggle theme">
                            {theme === 'light' ? <Moon className="w-4 h-4 sm:w-5 sm:h-5" /> : <Sun className="w-4 h-4 sm:w-5 sm:h-5" />}
                        </button>

                        {/* Account icon */}
                        <button onClick={handleAccountClick} className="relative text-foreground/80 hover:text-[#CEAC8F] transition-colors flex items-center gap-1" aria-label={isLoggedIn ? "My account" : "Sign in"}>
                            <User className="w-4 h-4 sm:w-5 sm:h-5" />
                            {isLoggedIn && (
                                <span className="text-xs text-[#CEAC8F] hidden lg:block">{customer?.firstName}</span>
                            )}
                        </button>

                        {/* Wishlist icon */}
                        <button
                            onClick={() => navigate(isLoggedIn ? '/wishlist' : '/login')}
                            className="relative text-foreground/80 hover:text-[#CEAC8F] transition-colors"
                            aria-label="Wishlist"
                        >
                            <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
                            {wishlistCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-[#CEAC8F] text-black text-[10px] sm:text-xs font-medium w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center">
                                    {wishlistCount}
                                </span>
                            )}
                        </button>

                        {/* Cart icon */}
                        <button onClick={() => setIsOpen(true)} className="relative text-foreground/80 hover:text-[#CEAC8F] transition-colors" aria-label={cartCount > 0 ? `Open cart, ${cartCount} item${cartCount === 1 ? '' : 's'}` : "Open cart"}>
                            <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
                            {cartCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-[#CEAC8F] text-black text-[10px] sm:text-xs font-medium w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center">
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </nav>

                    {/* Right — Cart only (mobile) */}
                    <div className="flex md:hidden items-center w-[40px] justify-end">
                        <button onClick={() => setIsOpen(true)} className="relative text-foreground/80 hover:text-[#CEAC8F] transition-colors" aria-label={cartCount > 0 ? `Open cart, ${cartCount} item${cartCount === 1 ? '' : 's'}` : "Open cart"}>
                            <ShoppingBag className="w-5 h-5" />
                            {cartCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-[#CEAC8F] text-black text-xs font-medium w-5 h-5 rounded-full flex items-center justify-center">
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile slide-out menu */}
            {isMobileMenuOpen && (
                <>
                    <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={closeMobileMenu} />
                    <div className="fixed left-0 w-80 bg-background z-50 md:hidden shadow-2xl overflow-y-auto" style={{ top: 'var(--banner-height, 34px)', height: 'calc(100% - var(--banner-height, 34px))' }}>
                        <div className="p-6">
                            <button onClick={closeMobileMenu} className="absolute top-6 right-6 text-foreground/80 hover:text-[#CEAC8F] transition-colors" aria-label="Close menu">
                                <X className="w-6 h-6" />
                            </button>
                            <div className="mb-12 mt-2">
                                <Link to="/" onClick={closeMobileMenu} className="text-2xl font-light tracking-wider text-[#CEAC8F]" style={{ fontFamily: "'Tenor Sans', sans-serif" }}>
                                    MAISON D'OUD
                                </Link>
                            </div>

                            <nav className="flex flex-col gap-6">
                                <Link to="/scents" onClick={closeMobileMenu} className={`text-lg tracking-wide transition-colors py-3 border-b border-border ${isActive('/scents') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                                    Our Scents
                                </Link>

                                {/* Collections dropdown — Atlas today, future collections join as siblings */}
                                <div className="border-b border-border py-3">
                                    <button
                                        onClick={() => setIsMobileCollectionsOpen(!isMobileCollectionsOpen)}
                                        className="w-full flex items-center justify-between text-lg tracking-wide text-foreground/80 hover:text-[#CEAC8F] transition-colors"
                                    >
                                        Collections
                                        <ChevronDown className={`w-4 h-4 transition-transform ${isMobileCollectionsOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isMobileCollectionsOpen && (
                                        <div className="mt-3 flex flex-col gap-3 pl-4">
                                            {collections.map((collection) => (
                                                <Link
                                                    key={collection.label}
                                                    to={collection.path}
                                                    onClick={closeMobileMenu}
                                                    className="text-sm tracking-wide text-foreground/70 hover:text-[#CEAC8F] transition-colors"
                                                >
                                                    {collection.label}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <Link to="/quiz" onClick={closeMobileMenu} className={`text-lg tracking-wide transition-colors py-3 border-b border-border ${isActive('/quiz') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                                    Find Your Scent
                                </Link>
                                <Link to="/locations" onClick={closeMobileMenu} className={`text-lg tracking-wide transition-colors py-3 border-b border-border ${isActive('/locations') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                                    Find Us
                                </Link>
                                <Link to="/contact" onClick={closeMobileMenu} className={`text-lg tracking-wide transition-colors py-3 border-b border-border ${isActive('/contact') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                                    Get in Touch
                                </Link>
                                <Link to={isLoggedIn ? '/wishlist' : '/login'} onClick={closeMobileMenu} className={`text-lg tracking-wide transition-colors py-3 border-b border-border ${isActive('/wishlist') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                                    {isLoggedIn ? `Wishlist${wishlistCount > 0 ? ` (${wishlistCount})` : ''}` : 'Wishlist'}
                                </Link>
                                <Link to={isLoggedIn ? '/account' : '/login'} onClick={closeMobileMenu} className={`text-lg tracking-wide transition-colors py-3 border-b border-border ${isActive('/account') || isActive('/login') ? 'text-[#CEAC8F]' : 'text-foreground/80 hover:text-[#CEAC8F]'}`}>
                                    {isLoggedIn ? `My Account (${customer?.firstName})` : 'Sign In'}
                                </Link>

                                {/* Theme toggle in mobile menu */}
                                <button
                                    onClick={() => { toggleTheme(); closeMobileMenu(); }}
                                    className="flex items-center gap-3 text-lg tracking-wide py-3 border-b border-border text-foreground/80 hover:text-[#CEAC8F] transition-colors text-left"
                                >
                                    {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                                    {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                                </button>

                                {/* Currency selector in mobile menu */}
                                <div className="py-3 border-b border-border">
                                    <p className="text-xs text-muted-foreground mb-3 tracking-wide uppercase">Currency</p>
                                    <div className="flex flex-wrap gap-2">
                                        {currencies.map((c) => (
                                            <button
                                                key={c.code}
                                                onClick={() => changeCurrency(c.code)}
                                                className={`px-3 py-1 rounded-lg text-sm border transition-colors ${
                                                    currency.code === c.code
                                                        ? 'border-[#CEAC8F] text-[#CEAC8F]'
                                                        : 'border-border text-foreground/60 hover:border-[#CEAC8F]/50'
                                                }`}
                                            >
                                                {c.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </nav>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default Header;